<template>
  <div class="position-relative" id="examResultAll">
    <neo-print-wrapper title="Exam Report">
      <a-spin :spinning="loading">
        <div class="row">
          <div class="col-12">
            <h4>{{ examData.exam.name }}</h4>
            <p class="mb-2">
              Class: <span class="font-weight-bold">{{ examData.school_class.name }}</span> |
              Section: <span class="font-weight-bold">{{ examData.section.name }}</span> |
              Total Students: <span class="font-weight-bold">{{ studentsData.length }}</span>
            </p>
          </div>

          <div class="col-12">
            <vue-custom-scrollbar class="scroll-area">
              <table class="table table-sm table-bordered">
                <thead>
                  <tr>
                    <th style="width: 5%">Roll</th>
                    <th style="width: 8%">Code</th>
                    <th style="width: 18%">Name</th>
                    <th
                      v-for="examSubject in examData.class_section_exam_subject"
                      :key="examSubject.id"
                      class="text-center"
                    >
                      {{ examSubject.subject.abbreviation }}
                    </th>
                    <th style="width: 7%; text-align: right; background: #f8f9fa;">Total</th>
                    <th style="width: 6%; text-align: right; background: #f8f9fa;">%</th>
                    <th style="width: 6%; text-align: center; background: #f8f9fa;">Sec Rank</th>
                    <th style="width: 6%; text-align: center; background: #f8f9fa;">Class Rank</th>
                    <th style="width: 7%; text-align: center; background: #f8f9fa;">Status</th>
                    <th style="width: 8%; text-align: center; background: #f8f9fa;">Attendance</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="student in studentsData"
                    :key="student.id"
                  >
                    <th scope="row">{{ student.roll_no || '--' }}</th>
                    <td>{{ student.student_code || '--' }}</td>
                    <td>
                      <div class="d-flex align-items-center justify-content-between">
                        <span>{{ student.student ? `${student.student.first_name} ${student.student.last_name}` : '--' }}</span>
                        <a-tag v-if="student.is_withheld" color="red" class="ml-1">Withheld</a-tag>
                      </div>
                    </td>
                    <td
                      v-for="examSubject in examData.class_section_exam_subject"
                      :key="examSubject.id"
                      class="text-center px-1"
                    >
                      <span :class="isMarkFailed(getStudentSubjectMark(examSubject.subject.id, student.student_exam_mark)) ? 'text-danger font-weight-bold' : ''">
                        {{ getStudentScore(getStudentSubjectMark(examSubject.subject.id, student.student_exam_mark)) }}
                      </span>
                    </td>
                    <th scope="row" class="text-right" style="background: #f8f9fa;">
                      {{ getStudentExamField(student, 'total_mark') }}
                    </th>
                    <td class="text-right font-weight-bold" style="background: #f8f9fa;">
                      {{ getStudentExamField(student, 'mark_percentage', '%') }}
                    </td>
                    <td class="text-center" style="background: #f8f9fa;">
                      {{ getStudentExamField(student, 'section_rank') }}
                    </td>
                    <td class="text-center" style="background: #f8f9fa;">
                      {{ getStudentExamField(student, 'class_rank') }}
                    </td>
                    <td class="text-center" style="background: #f8f9fa;">
                      <a-tag v-if="getStudentExamField(student, 'status') !== '--'" :color="getStudentExamField(student, 'status') === 'Pass' ? 'green' : 'red'">
                        {{ getStudentExamField(student, 'status') }}
                      </a-tag>
                      <span v-else>--</span>
                    </td>
                    <td class="text-center" style="background: #f8f9fa;">
                      {{ getStudentAttendance(student) }}
                    </td>
                  </tr>
                  <tr v-if="!studentsData.length">
                    <td :colspan="examData.class_section_exam_subject.length + 9">
                      <no-data text="No Students" />
                    </td>
                  </tr>
                </tbody>
              </table>
            </vue-custom-scrollbar>
          </div>
        </div>
      </a-spin>

      <!-- Print Button -->
      <div class="position-absolute neo-tr-5 d-print-none">
        <button
          type="button"
          class="btn btn-light btn-sm btn-with-addon"
          @click="print"
        >
          <span class="btn-addon">
            <i class="btn-addon-icon fe fe-printer" />
          </span>
          Print
        </button>
      </div>
    </neo-print-wrapper>
  </div>
</template>

<script>
import { find } from 'lodash'
import NoData from '@/components/layout/NoData/index'
import NeoPrintWrapper from '@/components/layout/Print/Default'

import vueCustomScrollbar from 'vue-custom-scrollbar'

export default {
  name: 'StudentMarksDisplayAll',
  props: ['examData', 'studentsData'],
  components: {
    vueCustomScrollbar,
    NoData,
    NeoPrintWrapper,
  },
  data: function() {
    return {
      loading: false,
    }
  },
  methods: {
    getStudentSubjectMark(subjectId, studentMarks) {
      if (!studentMarks) return null
      return find(studentMarks, { subject_id: subjectId })
    },
    getStudentScore(markData) {
      if (!markData) return '--'
      if (this.examData && this.examData.grades) {
        return markData.grade || markData.exam_mark_grade || '--'
      }
      if (markData.is_absent === 1 || markData.mark === 'Ab' || markData.theory_mark === 'Ab') return 'Ab'
      if (markData.is_absent === 2 || markData.mark === 'SL' || markData.theory_mark === 'SL') return 'SL'
      if (markData.mark !== null && markData.mark !== undefined) {
        return markData.mark
      }
      if (markData.theory_mark !== null && markData.theory_mark !== undefined) {
        return markData.theory_mark
      }
      if (markData.grade) {
        return markData.grade
      }
      return '--'
    },
    isMarkFailed(markData) {
      if (!markData) return false
      return Boolean(markData.is_failed || markData.is_absent || markData.mark === 'Ab' || markData.mark === 'SL' || markData.theory_mark === 'Ab' || markData.theory_mark === 'SL')
    },
    getStudentExamField(student, field, suffix = '') {
      const exam = student.student_exam && student.student_exam.length ? student.student_exam[0] : null
      if (!exam || exam[field] === null || exam[field] === undefined) return '--'
      return `${exam[field]}${suffix}`
    },
    getStudentAttendance(student) {
      const exam = student.student_exam && student.student_exam.length ? student.student_exam[0] : null
      if (!exam || exam.attendance === null || exam.attendance === undefined) return '--'
      return exam.total_school_days ? `${exam.attendance} / ${exam.total_school_days}` : `${exam.attendance}`
    },
    print() {
      this.$htmlToPaper('examResultAll')
    },
  },
}
</script>

<style lang="scss" scoped>
  .scroll-area {
    position: relative;
    margin: auto;
  }
</style>
